import React from "react";
import { motion } from "framer-motion";

import termsData from "./termsData";
import TimelineItem from "./TimelineItem";
import AgreementCard from "./AgreementCard";

const faqData = [
  {
    question: "Who owns the intellectual property after project completion?",
    answer:
      "Ownership of project deliverables is determined by the signed agreement between Web_Bocket and the client. Upon successful project completion and fulfillment of all payment obligations, ownership rights are transferred in accordance with the agreed contractual terms unless otherwise specified.",
  },
  {
    question: "How does Web_Bocket protect confidential information?",
    answer:
      "We treat all client information, project documentation, business strategies, and technical assets as confidential. Sensitive information is handled using industry best practices and, where applicable, Non-Disclosure Agreements (NDAs) to ensure data privacy and security.",
  },
  {
    question: "What are the client's responsibilities during a project?",
    answer:
      "Clients are expected to provide accurate project requirements, timely approvals, required content, and prompt feedback throughout the project lifecycle. Delays in providing necessary information or approvals may impact delivery timelines.",
  },
  {
    question: "Are project timelines guaranteed?",
    answer:
      "Project timelines are estimated based on the agreed scope of work and available information. While we strive to meet every milestone, timelines may be adjusted due to scope changes, client-requested revisions, unforeseen technical challenges, or delays in receiving required approvals.",
  },
  {
    question: "What happens if the project scope changes?",
    answer:
      "Any request that extends beyond the originally approved project scope may require additional time, resources, and cost. Scope changes will be reviewed, documented, and implemented only after mutual agreement between Web_Bocket and the client.",
  },
  {
    question: "How can I request clarification regarding these Terms?",
    answer:
      "If you require clarification about any provision within these Terms & Conditions, you may contact our team through the Contact page. We are committed to providing clear, transparent, and timely responses to all legal and service-related inquiries.",
  },
];

const TermsConditions = () => {
  return (
    <div
      className="
        relative
        min-h-screen
        overflow-hidden
        bg-[var(--bg-primary)]
        font-saira
        text-[var(--text-primary)]
      "
    >
      {/* Background Glow */}

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            x: [-20, 20, -20],
            y: [-10, 15, -10],
          }}
          transition={{
            repeat: Infinity,
            duration: 14,
            ease: "linear",
          }}
          className="
            absolute
            left-[-120px]
            top-[-120px]
            h-80
            w-80
            rounded-full
            bg-[var(--brand-primary)]/10
            blur-[130px]
          "
        />

        <motion.div
          animate={{
            x: [20, -20, 20],
            y: [10, -15, 10],
          }}
          transition={{
            repeat: Infinity,
            duration: 16,
            ease: "linear",
          }}
          className="
            absolute
            bottom-0
            right-[-100px]
            h-96
            w-96
            rounded-full
            bg-[var(--brand-primary)]/10
            blur-[150px]
          "
        />
      </div>

      {/* Hero */}

      <section className="relative z-10 pt-28 pb-16 md:pt-32 md:pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{
              opacity: 0,
              y: 40,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.7,
            }}
            className="text-center"
          >
            <motion.p
              initial={{
                opacity: 0,
                letterSpacing: "0.1em",
              }}
              animate={{
                opacity: 1,
                letterSpacing: "0.3em",
              }}
              transition={{
                duration: 0.5,
              }}
              className="
                text-[11px]
                font-bold
                uppercase
                tracking-[0.3em]
                text-primaryColor
                sm:text-xs
                md:text-sm
              "
            >
              [ Terms of Service ]
            </motion.p>

            <motion.h1
              initial={{
                opacity: 0,
                y: 20,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.15,
                duration: 0.6,
              }}
              className="
                mx-auto
                mt-5
                max-w-5xl
                text-3xl
                font-extrabold
                leading-tight
                sm:text-4xl
                md:text-5xl
                lg:text-6xl
              "
            >
              Terms &
              <span className="text-[var(--brand-primary)]"> Conditions</span>
            </motion.h1>

            <motion.p
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              transition={{
                delay: 0.3,
                duration: 0.6,
              }}
              className="
                mx-auto
                mt-6
                max-w-3xl
                text-sm
                leading-7
                text-[var(--text-muted)]
                sm:text-base
                sm:leading-8
                lg:text-lg
              "
            >
              These Terms & Conditions define the legal relationship between
              Web_Bocket and every client, visitor, or organization using our
              website and professional software development services.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Agreement Section */}

      <section className="relative z-10 pb-14 md:pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <AgreementCard />
        </div>
      </section>

      {/* Terms Timeline */}

      <section className="relative z-10 overflow-hidden pb-16 md:pb-20">
        {/* Background Glow */}

        <motion.div
          animate={{
            x: [-20, 20, -20],
            y: [-10, 15, -10],
          }}
          transition={{
            repeat: Infinity,
            duration: 18,
            ease: "linear",
          }}
          className="
            absolute
            left-0
            top-0
            h-80
            w-80
            rounded-full
            bg-[var(--brand-primary)]/5
            blur-[130px]
          "
        />

        <motion.div
          animate={{
            x: [15, -20, 15],
            y: [10, -10, 10],
          }}
          transition={{
            repeat: Infinity,
            duration: 22,
            ease: "linear",
          }}
          className="
            absolute
            right-0
            bottom-0
            h-80
            w-80
            rounded-full
            bg-[var(--brand-light)]/5
            blur-[130px]
          "
        />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Section Heading */}

          <motion.div
            initial={{ opacity: 0, y: 35 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-14 text-center md:mb-16"
          >
            <motion.p
              initial={{
                opacity: 0,
                letterSpacing: "0.1em",
              }}
              whileInView={{
                opacity: 1,
                letterSpacing: "0.3em",
              }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="
                text-[11px]
                font-bold
                uppercase
                tracking-[0.3em]
                text-primaryColor
                sm:text-xs
                md:text-sm
              "
            >
              [ Legal Framework ]
            </motion.p>

            <motion.h2
              initial={{
                opacity: 0,
                y: 20,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{ once: true }}
              transition={{
                duration: 0.6,
                delay: 0.1,
              }}
              className="
                mt-4
                text-3xl
                font-bold
                sm:text-4xl
                md:text-5xl
                lg:text-6xl
              "
            >
              Understanding Our
              <span className="text-[var(--brand-primary)]"> Terms</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.6,
                delay: 0.25,
              }}
              className="
                mx-auto
                mt-4
                max-w-3xl
                text-base
                leading-7
                text-[var(--text-muted)]
                sm:text-lg
              "
            >
              The following sections outline the fundamental terms that govern
              every project, partnership, and service provided by Web_Bocket.
              These guidelines promote transparency, accountability, and
              long-term business relationships.
            </motion.p>
          </motion.div>

          {/* Timeline */}

          <div className="mx-auto max-w-5xl">
            {termsData.map((item, index) => (
              <TimelineItem key={item.number} item={item} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}

      <section className="relative z-10 pb-16 md:pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{
              opacity: 0,
              y: 45,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{
              duration: 0.7,
            }}
            whileHover={{
              y: -4,
            }}
            className="
        group
        relative
        overflow-hidden
        rounded-3xl

        /* Theme-aware border */
        border
        border-[var(--border-primary)]

        bg-gradient-to-br
        from-[var(--card-bg)]
        via-[var(--card-bg-secondary)]
        to-[var(--bg-tertiary)]

        px-4
        py-10
        text-center
        transition-all
        duration-500

        sm:px-6
        lg:px-8
        md:px-12
        md:py-14
      "
          >
            {/* ============================================================
          HOVER GLOW
      ============================================================ */}
            <div
              className="
          absolute
          inset-0
          bg-[radial-gradient(circle_at_top_right,rgba(249,115,22,0.125),transparent_72%)]
          opacity-0
          transition-opacity
          duration-500
          group-hover:opacity-100
        "
            />

            {/* ============================================================
          TOP ORANGE GLOW
      ============================================================ */}
            <motion.div
              animate={{
                x: [-15, 15, -15],
                y: [-8, 8, -8],
              }}
              transition={{
                repeat: Infinity,
                duration: 12,
                ease: "linear",
              }}
              className="
          absolute
          -top-20
          right-0
          h-60
          w-60
          rounded-full
          bg-[var(--brand-primary)]/10
          blur-[120px]
        "
            />

            {/* ============================================================
          BOTTOM ORANGE GLOW
      ============================================================ */}
            <motion.div
              animate={{
                x: [10, -15, 10],
                y: [8, -8, 8],
              }}
              transition={{
                repeat: Infinity,
                duration: 14,
                ease: "linear",
              }}
              className="
          absolute
          -bottom-20
          left-0
          h-60
          w-60
          rounded-full
          bg-[var(--brand-primary)]/5
          blur-[120px]
        "
            />

            {/* ============================================================
          CONTENT
      ============================================================ */}
            <div className="relative z-10">
              {/* Small Label */}
              <motion.p
                initial={{
                  opacity: 0,
                  letterSpacing: "0.1em",
                }}
                whileInView={{
                  opacity: 1,
                  letterSpacing: "0.3em",
                }}
                transition={{
                  duration: 0.5,
                }}
                className="
            text-[11px]
            font-bold
            uppercase
            tracking-[0.3em]
            text-primaryColor

            sm:text-xs
            md:text-sm
          "
              >
                [ Need Assistance ? ]
              </motion.p>

              {/* Heading */}
              <motion.h2
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  delay: 0.15,
                }}
                className="
            mt-5
            text-3xl
            font-bold
            leading-tight

            sm:text-4xl
            md:text-5xl
            lg:text-6xl
          "
              >
                Questions About Our{" "}
                <span className="text-[var(--brand-primary)]">
                  Terms & Conditions?
                </span>
              </motion.h2>

              {/* Description */}
              <motion.p
                initial={{
                  opacity: 0,
                }}
                whileInView={{
                  opacity: 1,
                }}
                transition={{
                  delay: 0.3,
                }}
                className="
            mx-auto
            mt-5
            max-w-3xl
            text-base
            leading-7
            text-[var(--text-muted)]

            sm:text-lg
          "
              >
                If you have questions regarding these Terms & Conditions or
                require clarification before engaging with our services, our
                team is ready to assist you. We believe in complete transparency
                and are happy to provide any additional information you may
                need.
              </motion.p>

              {/* Contact Button */}
              <motion.button
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 0 30px rgba(249,115,22,.35)",
                }}
                whileTap={{
                  scale: 0.95,
                }}
                transition={{
                  duration: 0.25,
                }}
                onClick={() => {
                  window.location.href = "/contact";
                  window.scrollTo(0, 0);
                }}
                className="
            mt-8
            rounded-xl
            bg-[var(--brand-primary)]
            px-6
            py-3
            text-base
            font-semibold
            text-white
            transition-all
            duration-300
            hover:bg-[var(--brand-hover)]

            sm:px-8
            sm:py-4
            sm:text-lg
          "
              >
                Contact Us
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;
